import React, { useMemo } from "react";
import {
  Wrap,
  Section,
  PanelTitle,
  Grid,
  ProductCard,
  ProductImg,
  ProductBody,
  ProductName,
  ProductDesc,
  ProductFooter,
  ProductPriceWrap,
  ProductPrice,
  ProductOldPrice,
  DiscountBadge,
  AddBtn,
  Empty,
  ProductGramaj,
} from "./CategoryProductsStyles";
import { FiShoppingCart } from "react-icons/fi";

const bpMd = 768;

function formatGramaj(gramaj) {
  const value = Number(gramaj);

  if (!Number.isFinite(value) || value <= 0) return "";

  const pretty = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2).replace(/\.?0+$/, "");

  return `Cantitate (+/- 10%): ${pretty} g`;
}

function isVariablePrice(product) {
  return product.tip_pret === "variabil" || product.tip_pret === "kg";
}

function formatLei(value) {
  const n = Number(value || 0);

  return n.toLocaleString("ro-RO", {
    minimumFractionDigits: n % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
}

function getDiscountedPrice(product) {
  const pret = Number(product.pret || 0);
  const reducereTip = product.reducere_tip || "procent";
  const reducere = Number(product.reducere_procent || 0);
  const pretRedus = Number(product.pret_redus || 0);

  if (reducereTip === "pret_nou" && pretRedus > 0 && pretRedus < pret) {
    return pretRedus;
  }

  if (reducereTip === "procent" && reducere > 0) {
    return Math.round((pret - (pret * reducere) / 100) * 2) / 2;
  }

  return pret;
}

function getDiscountBadge(product) {
  const pret = Number(product.pret || 0);
  const reducereTip = product.reducere_tip || "procent";

  if (reducereTip === "procent") {
    return `-${Number(product.reducere_procent || 0)}%`;
  }

  const pretRedus = Number(product.pret_redus || 0);
  if (pret > 0 && pretRedus > 0 && pretRedus < pret) {
    const procent = Math.round(((pret - pretRedus) / pret) * 100);
    return `-${procent}%`;
  }

  return "";
}

function formatPrice(product) {
  const pretFinal = getDiscountedPrice(product);

  if (product.afiseaza_pret_variabil) {
    return "Preț variabil";
  }

  if (isVariablePrice(product)) {
    return `${formatLei(pretFinal)} lei / kg`;
  }

  if (product.afiseaza_per_buc) {
    return `${formatLei(pretFinal)} lei / buc`;
  }

  return `${formatLei(pretFinal)} lei`;
}

function formatOldPrice(product) {
  if (product.afiseaza_pret_variabil) {
    return "";
  }

  const pret = Number(product.pret || 0);

  if (isVariablePrice(product)) {
    return `${formatLei(pret)} lei / kg`;
  }

  if (product.afiseaza_per_buc) {
    return `${formatLei(pret)} lei / buc`;
  }

  return `${formatLei(pret)} lei`;
}

export default function CategoryProducts({
  categories = [],
  products = [],
  activeId = null,
  modeDesktop = "filter",
  modeMobile = "all",
  allId = "__all__",
  onAddToCart,
  onOpenProduct,
}) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < bpMd;
  const mode = isMobile ? modeMobile : modeDesktop;

  const byCat = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, []));
    products.forEach((p) => {
      if (!map.has(p.categorie)) map.set(p.categorie, []);
      map.get(p.categorie).push(p);
    });
    return map;
  }, [categories, products]);

  const listCats = useMemo(() => {
    if (!categories.length) return [];

    if (mode === "all" || activeId === allId) {
      return categories.filter((c) => c.id !== allId);
    }

    const found = categories.find((c) => c.id === activeId);
    return found ? [found] : categories;
  }, [categories, activeId, mode, allId]);

  if (!categories.length) return null;

  return (
    <Wrap>
      {listCats.map((cat) => {
        const items = byCat.get(cat.id) || [];

        return (
          <Section key={cat.id} id={`cat-${cat.id}`} data-cat={cat.id}>
            <PanelTitle>{cat.name}</PanelTitle>

            {items.length ? (
              <Grid>
                {items.map((p) => {
                  const esteInStoc =
                    (p.disponibil ?? true) && Number(p.stoc ?? 0) > 0;

                  const hasDiscount =
                    !p.afiseaza_pret_variabil &&
                    ((p.reducere_tip === "procent" &&
                      Number(p.reducere_procent || 0) > 0) ||
                      (p.reducere_tip === "pret_nou" &&
                        Number(p.pret_redus || 0) > 0 &&
                        Number(p.pret_redus || 0) < Number(p.pret || 0)));

                  const coverImage =
                    p.imagini?.[0]?.image_url ||
                    p.imagine ||
                    "/placeholder.jpg";

                  return (
                    <ProductCard key={p.id} onClick={() => onOpenProduct?.(p)}>
                      {hasDiscount && (
                        <DiscountBadge>{getDiscountBadge(p)}</DiscountBadge>
                      )}

                      <div className="imgWrap">
                        <ProductImg
                          src={coverImage}
                          alt={p.nume}
                          loading="lazy"
                        />
                      </div>

                      <ProductBody>
                        <ProductName>{p.nume}</ProductName>
                        <ProductDesc>{p.descriere}</ProductDesc>

                        {!p.gramaj_selectabil && !!p.gramaj && (
                          <ProductGramaj>
                            {formatGramaj(p.gramaj)}
                          </ProductGramaj>
                        )}
                      </ProductBody>

                      <ProductFooter>
                        <ProductPriceWrap>
                          {hasDiscount && !p.afiseaza_pret_variabil && (
                            <ProductOldPrice>
                              {formatOldPrice(p)}
                            </ProductOldPrice>
                          )}
                          <ProductPrice>{formatPrice(p)}</ProductPrice>
                        </ProductPriceWrap>

                        <AddBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            if (esteInStoc) onAddToCart?.(p);
                          }}
                          disabled={!esteInStoc}
                        >
                          {esteInStoc ? "Adaugă în coș" : "Stoc epuizat"}
                          <FiShoppingCart />
                        </AddBtn>
                      </ProductFooter>
                    </ProductCard>
                  );
                })}
              </Grid>
            ) : (
              <Empty>Nu există produse în categoria asta (încă).</Empty>
            )}
          </Section>
        );
      })}
    </Wrap>
  );
}
